import { useStoredState } from "@/hooks/useStoredState";
import { ToolShell } from "@/components/tools/ToolShell";
import { AutoTextarea, TextField } from "@/components/tools/fields";
import { RowsEditor, SubHead, blankRows } from "@/components/tools/blocks";

export const Info = ({ items }) => (
  <dl className="mb-6 grid gap-3 md:grid-cols-3">
    {items.map(([k, v]) => (
      <div key={k} className="avoid-break rounded-2xl border-2 border-line bg-surface p-4">
        <dt className="eyebrow mb-1">{k}</dt>
        <dd className="text-sm leading-relaxed">{v}</dd>
      </div>
    ))}
  </dl>
);

const Field = ({ id, f, value, onChange }) => (
  f.type === "number" || f.type === "date" || f.type === "text"
    ? <TextField id={`${id}-${f.k}`} label={f.label} type={f.type} inputMode={f.type === "number" ? "numeric" : undefined} min={f.type === "number" ? 0 : undefined} value={value} onChange={onChange} testid={`${id}-${f.k}`} />
    : <AutoTextarea id={`${id}-${f.k}`} label={f.label} rows={f.rows || 3} value={value} onChange={onChange} testid={`${id}-${f.k}`} />
);

const initFor = (tables) => Object.fromEntries((tables || []).map((t) => [t.name, t.seed ? t.seed.map((s, i) => ({ id: `r${i + 1}`, ...s })) : blankRows(t.rows || 2)]));

/**
 * groups: [{ letter, title, hint, cols, fields: [{k, label, rows, type}] }]
 * tables: [{ name, letter, title, hint, cols, rows, grid, addLabel }]
 */
export const SimpleTool = ({ id, tool, storageKey, groups = [], tables = [], intro, children, metaFields = true, tablesFirst = false }) => {
  const [data, setData, reset] = useStoredState(storageKey, initFor(tables));
  const set = (k) => (v) => setData({ ...data, [k]: v });
  const groupEls = groups.map((g) => (
    <div key={g.title} className="avoid-break">
      <SubHead letter={g.letter} title={g.title}>{g.hint}</SubHead>
      <div className={`grid gap-4 ${g.cols || "md:grid-cols-2"}`}>
        {g.fields.map((f) => <Field key={f.k} id={id} f={f} value={data[f.k]} onChange={set(f.k)} />)}
      </div>
    </div>
  ));
  const tableEls = tables.map((t) => (
    <div key={t.name}>
      <SubHead letter={t.letter} title={t.title}>{t.hint}</SubHead>
      <RowsEditor name={`${id}-${t.name}`} rows={data[t.name]} setRows={set(t.name)} cols={t.cols} addLabel={t.addLabel} grid={t.grid} />
    </div>
  ));
  return (
    <ToolShell id={id} tool={tool} onReset={reset} metaFields={metaFields}>
      {intro}
      {tablesFirst ? <>{tableEls}{groupEls}</> : <>{groupEls}{tableEls}</>}
      {children?.(data, setData)}
    </ToolShell>
  );
};
